import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Onboarding.css";
import {
  Wand2 as Magic,
  Check,
  Rocket,
  Briefcase,
  Settings as Cog,
  Plus,
  Lightbulb,
  Trash2 as Trash,
  Lock,
} from "lucide-react";
import { useProjectMemberMutation, useLazySearchOneRecordQuery, useCreateProjectMutation } from "../../services/rtkClient";
import { toast } from "react-toastify";

const Onboarding = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [projectName, setProjectName] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("default");

  const getCurrentUser = () => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) return JSON.parse(storedUser);
    } catch (err) { }
    return null;
  };

  const currentUserInfo = getCurrentUser();

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setUserEmail(user.email || user.username || "default");
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
  }, []);

  // Step 3 state
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("Developer");
  const [members, setMembers] = useState([]);

  // Auto-select first template after 2 seconds (demo behavior from original script)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!selectedTemplate) {
        // Uncomment to auto-select
        // setSelectedTemplate('product-launch');
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [selectedTemplate]);

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
  };

  const [inviteMembers] = useProjectMemberMutation();
  const [searchOneRecord] = useLazySearchOneRecordQuery();
  const [createProject] = useCreateProjectMutation();

  const handleContinue = async () => {
    if (step === 1 && selectedTemplate) {
      setStep(2);
    } else if (step === 2 && projectName.trim()) {
      setStep(3);
    } else if (step === 3) {
      try {
        // 1. Create project on server
        const trimmedName = projectName.trim();
        const baseKey = trimmedName.substring(0, 4).toUpperCase().replace(/\s+/g, '');
        const randomNum = Math.floor(100 + Math.random() * 900); // 3-digit random number
        const projectKey = `${baseKey}${randomNum}`;

        // Provide static organization id and name as requested
        let organizationId = 1;
        let organizationName = "Static Organization";

        // Map frontend templates to allowed backend projectType enums
        let backendProjectType = "custom";
        if (selectedTemplate === "software-development") {
          backendProjectType = "scrum";
        } else if (selectedTemplate === "marketing-campaign" || selectedTemplate === "product-launch") {
          backendProjectType = "kanban";
        }

        const projectResponse = await createProject({
          name: trimmedName,
          projectName: trimmedName,
          key: projectKey,
          projectKey: projectKey,
          projectType: backendProjectType,
          visibility: "public",
          status: "active",
          deletedAt: "9999-12-31T23:59:59.999Z",
          description: `Project created from template: ${selectedTemplate || "custom"}`,
          organizationId: organizationId,
          organizationName: organizationName,
        }).unwrap();

        const serverProject = projectResponse?.data || projectResponse?.schema || projectResponse;
        const newProjectId = serverProject?.id || serverProject?.projectId;

        // 2. Invite members if any
        if (members.length > 0 && newProjectId) {
          const invitationBody = members.map(m => ({
            projectId: Number(newProjectId),
            userId: Number(m.userId) || 1,
            role: m.role.toLowerCase(),
            permissions: {},
            addedBy: Number(currentUserInfo?.id || currentUserInfo?.userId || currentUserInfo?._id || 1),
            addedAt: new Date().toISOString()
          }));

          try {
            await inviteMembers(invitationBody).unwrap();
          } catch (memberErr) {
            console.error("Member Invite Failed:", memberErr);
            // We ignore failure here to let them proceed to /tasks even if invites fail
          }
        }

        // Local storage fallback removed per user request
        sessionStorage.setItem("projectId", newProjectId || 1);
        sessionStorage.setItem("projectName", projectName.trim());

        toast.success("Project setup complete!");
        navigate("/tasks", { state: { projectId: newProjectId || 1, projectName: projectName.trim(), openCreateTask: true } });
      } catch (err) {
        console.error("Setup Error - Full:", err);
        console.error("Setup Error - Data:", err?.data);
        console.error("Setup Error - Status:", err?.status);

        let errorMsg = "Failed to complete setup. Please try again.";
        if (err?.data?.msg) {
          errorMsg = err.data.msg;
        } else if (typeof err?.data === 'string') {
          errorMsg = err.data;
        } else if (err?.data && !err.data.status) {
          errorMsg = JSON.stringify(err.data);
        }

        toast.error(errorMsg);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleInvite = async () => {
    const emailInput = inviteEmail.trim();
    if (!emailInput) {
      toast.warning("Please enter an email address.");
      return;
    }

    try {
      // 1. Search if user exists using searchOneRecord
      const searchResponse = await searchOneRecord(emailInput).unwrap();

      console.log("SEARCH RESPONSE:", searchResponse);

      // Check for email in different possible response structures
      const foundEmail = searchResponse.schema?.email || searchResponse.email || (searchResponse.data && searchResponse.data.email);
      const foundUserId = searchResponse.schema?.id || searchResponse.id || searchResponse.schema?.userId || searchResponse.userId || (searchResponse.data && searchResponse.data.id) || 1;

      if (!foundEmail) {
        toast.error("User is not registered");
        return;
      }

      // Ensure email isn't already added
      if (members.some(m => m.email.toLowerCase() === foundEmail.toLowerCase())) {
        toast.info("User is already in the project members list.");
        return;
      }

      // Add user to the members array
      setMembers([...members, { email: foundEmail, userId: foundUserId, role: inviteRole }]);
      setInviteEmail("");
      toast.success("User added successfully!");
    } catch (err) {
      console.error("Invite API Error:", err);
      toast.error("User is not registered");
      return;
    }
  };

  const handleRemoveMember = (email) => {
    setMembers(members.filter((m) => m.email !== email));
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else if (
      window.confirm(
        "Are you sure you want to go back? Your selection will be lost.",
      )
    ) {
      navigate(-1);
    }
  };

  const handleSkip = () => {
    navigate("/tasks"); // Assuming dashboard is the main app area
  };

  return (
    <div className="onboarding-page-wrapper">
      <div className="header">
        <div className="logo-container">
          <svg
            className="product-icon"
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M 20 35 L 45 35"
              stroke="#1E40AF"
              strokeWidth="4"
              strokeLinecap="round"
            ></path>
            <path
              d="M 20 50 L 55 50"
              stroke="#14B8A6"
              strokeWidth="4"
              strokeLinecap="round"
            ></path>
            <path
              d="M 20 65 L 65 65"
              stroke="#F59E0B"
              strokeWidth="4"
              strokeLinecap="round"
            ></path>
            <path d="M 45 35 L 55 40 L 45 45 Z" fill="#1E40AF"></path>
            <path d="M 55 50 L 65 55 L 55 60 Z" fill="#14B8A6"></path>
            <path d="M 65 65 L 75 70 L 65 75 Z" fill="#F59E0B"></path>
            <circle cx="80" cy="50" r="8" fill="#1E40AF"></circle>
          </svg>
          <span className="logo-text">SequFlow</span>
        </div>
        <div className="header-actions">
          <button className="skip-btn" onClick={handleSkip}>
            Skip for now
          </button>
        </div>
      </div>

      <div className="container">
        <div className="welcome-section">
          <div className="welcome-badge">👋 Welcome aboard!</div>
          <h1 className="welcome-title">
            {step === 1
              ? "Let's create your first project"
              : step === 2
                ? "Project details"
                : "Invite your team members"}
          </h1>
          <p className="welcome-subtitle">
            {step === 1
              ? "Choose a template to get started quickly, or create a custom project from scratch. Don't worry, you can always customize everything later."
              : step === 2
                ? "Give your project a name and set things up for success"
                : "Add people to collaborate on this project"}
          </p>
        </div>

        <div className="progress-steps">
          <div
            className={`step ${step >= 1 ? "active" : ""} ${step > 1 ? "completed" : ""}`}
          >
            <div className="step-circle">{step > 1 ? <Check size={16} /> : "1"}</div>
            <div className="step-label">Choose Template</div>
          </div>
          <div
            className={`step ${step >= 2 ? "active" : ""} ${step > 2 ? "completed" : ""}`}
          >
            <div className="step-circle">{step > 2 ? <Check size={16} /> : "2"}</div>
            <div className="step-label">Project Details</div>
          </div>
          <div className={`step ${step >= 3 ? "active" : ""}`}>
            <div className="step-circle">3</div>
            <div className="step-label">Invite Team</div>
          </div>
        </div>

        <div className="content-section">
          {step === 1 ? (
            <>
              <div className="section-header">
                <h2 className="section-title">Popular templates</h2>
                <p className="section-description">
                  Select a pre-built workflow template designed for your team's
                  needs
                </p>
              </div>

              <div className="templates-grid">
                <div
                  className={`template-card ${selectedTemplate === "product-launch" ? "selected" : ""}`}
                  onClick={() => handleTemplateSelect("product-launch")}
                >
                  <div className="check-icon">
                    <Check size={14} />
                  </div>
                  <div className="template-icon">
                    <Rocket size={32} />
                  </div>
                  <h3 className="template-name">Product Launch</h3>
                  <p className="template-description">
                    Perfect for coordinating product releases with multiple
                    teams and milestones.
                  </p>
                  <div className="template-features">
                    <span className="feature-tag">Task tracking</span>
                    <span className="feature-tag">Timeline view</span>
                    <span className="feature-tag">Team collaboration</span>
                  </div>
                </div>

                <div
                  className={`template-card ${selectedTemplate === "marketing-campaign" ? "selected" : ""}`}
                  onClick={() => handleTemplateSelect("marketing-campaign")}
                >
                  <div className="check-icon">
                    <Check size={14} />
                  </div>
                  <div className="template-icon">
                    <Briefcase size={32} />
                  </div>
                  <h3 className="template-name">Marketing Campaign</h3>
                  <p className="template-description">
                    Plan and execute marketing campaigns with content calendars
                    and asset management.
                  </p>
                  <div className="template-features">
                    <span className="feature-tag">Content calendar</span>
                    <span className="feature-tag">Analytics</span>
                    <span className="feature-tag">File sharing</span>
                  </div>
                </div>

                <div
                  className={`template-card ${selectedTemplate === "software-development" ? "selected" : ""}`}
                  onClick={() => handleTemplateSelect("software-development")}
                >
                  <div className="check-icon">
                    <Check size={14} />
                  </div>
                  <div className="template-icon">
                    <Cog size={32} />
                  </div>
                  <h3 className="template-name">Software Development</h3>
                  <p className="template-description">
                    Manage sprints, track bugs, and coordinate releases with
                    your dev team.
                  </p>
                  <div className="template-features">
                    <span className="feature-tag">Sprint planning</span>
                    <span className="feature-tag">Bug tracking</span>
                    <span className="feature-tag">Code reviews</span>
                  </div>
                </div>

                <div
                  className={`template-card custom-project ${selectedTemplate === "custom" ? "selected" : ""}`}
                  onClick={() => handleTemplateSelect("custom")}
                >
                  <div className="check-icon">
                    <Check size={14} />
                  </div>
                  <div className="custom-icon">
                    <Plus size={32} />
                  </div>
                  <h3 className="template-name">Start from scratch</h3>
                  <p className="template-description">
                    Create a custom workflow tailored to your specific needs
                  </p>
                </div>
              </div>

              <div className="tips-section">
                <div className="tips-title">
                  <Lightbulb size={20} /> Pro tips for getting started
                </div>
                <ul className="tips-list">
                  <li>
                    Templates are fully customizable - add or remove stages as
                    needed
                  </li>
                  <li>
                    You can switch templates later without losing your data
                  </li>
                  <li>
                    Invite team members after setup to start collaborating
                    immediately
                  </li>
                </ul>
              </div>
            </>
          ) : step === 2 ? (
            <div className="project-details-step">
              <div className="project-form">
                <div className="form-group">
                  <label
                    className="form-label"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                    }}
                  >
                    Project Name
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Website Redesign 2024"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    autoFocus
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      border: "2px solid #E5E7EB",
                      fontSize: "16px",
                      transition: "border-color 0.3s",
                    }}
                  />
                  <p
                    className="help-text"
                    style={{
                      fontSize: "13px",
                      color: "#6B7280",
                      marginTop: "8px",
                    }}
                  >
                    A good name helps your team stay organized
                  </p>
                </div>

                <div
                  className="selected-template-preview"
                  style={{
                    marginTop: "32px",
                    padding: "16px",
                    background: "#F9FAFB",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <div
                    className="template-icon"
                    style={{ marginBottom: 0, width: "40px", height: "40px" }}
                  >
                    {selectedTemplate === "product-launch" && <Rocket size={24} />}
                    {selectedTemplate === "marketing-campaign" && (
                      <Briefcase size={24} />
                    )}
                    {selectedTemplate === "software-development" && <Cog size={24} />}
                    {selectedTemplate === "custom" && <Plus size={24} />}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6B7280",
                        textTransform: "uppercase",
                      }}
                    >
                      Selected Template
                    </div>
                    <div style={{ fontWeight: "700" }}>
                      {selectedTemplate === "product-launch" &&
                        "Product Launch"}
                      {selectedTemplate === "marketing-campaign" &&
                        "Marketing Campaign"}
                      {selectedTemplate === "software-development" &&
                        "Software Development"}
                      {selectedTemplate === "custom" && "Custom Project"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="invite-team-step">
              <h2 className="section-title">Invite team members</h2>
              <div className="invite-form">
                <input
                  type="email"
                  className="form-input invite-email"
                  placeholder="Enter email address"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
                <div className="custom-select-wrapper">
                  <select
                    className="form-select invite-role"
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                  >
                    <option value="Developer">Developer</option>
                    <option value="Tester">Tester</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
                <button className="btn-invite" onClick={handleInvite}>
                  Invite
                </button>
              </div>

              <h2 className="section-title" style={{ marginTop: "32px" }}>
                Added Members
              </h2>
              <div className="members-table-container">
                <table className="members-table">
                  <thead>
                    <tr>
                      <th>Name / Email</th>
                      <th>Role</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member, index) => (
                      <tr key={index}>
                        <td>
                          <div className="member-info">
                            <div
                              className="member-avatar"
                              style={{ backgroundColor: "#3b82f6" }}
                            >
                              {member.email.charAt(0).toUpperCase()}
                            </div>
                            <div className="member-details">
                              <div className="member-name">{member.email}</div>
                              <div
                                className="member-email"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                <Lock
                                  size={10}
                                  className="lock-icon"
                                  style={{ color: "#6B7280" }}
                                />{" "}
                                {member.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`role-badge role-${member.role.toLowerCase()}`}
                          >
                            {member.role}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn-remove"
                            onClick={() => handleRemoveMember(member.email)}
                          >
                            Remove{" "}
                            <Trash
                              size={14}
                              style={{ marginLeft: "4px" }}
                            />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div
          className="actions"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {step === 3 ? (
            <button className="btn btn-secondary" onClick={handleSkip}>
              Skip for now
            </button>
          ) : (
            <button className="btn btn-secondary" onClick={handleBack}>
              Back
            </button>
          )}

          <button
            className="btn btn-primary"
            id="continueBtn"
            disabled={
              (step === 1 && !selectedTemplate) ||
              (step === 2 && !projectName) ||
              isLoading
            }
            onClick={handleContinue}
          >
            {isLoading
              ? "Processing..."
              : step === 1
                ? "Continue to project details →"
                : step === 2
                  ? "Continue to invite team →"
                  : "Finish Setup →"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
